"use client";

import { useRouter } from "next/navigation";
import React, { type PropsWithChildren, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { twMerge } from "tailwind-merge";

import { env } from "@/env";
import { getEnumValue, getFormMaxlength, toErrorString } from "@/globals";
import { NEWSLETTER_SCHOOLFORM } from "@/schemes/form-newsletter-module.scheme";
import { BUTTON_STYLES } from "@/styles/buttons";
import { INPUT_STYLES, TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";
import { ErrorView } from "@/views/error.view";

type FormNewlsetterModuleProps = {
    buttonLabel: string;
    entityId: string;
    entityType: string;
    roles: Record<string, string>;
    subjects: Record<string, Record<string, string>>;
    successHref: string | undefined;
};

type FormProps = {
    [field: string]: string | boolean | undefined;
    email?: string;
    entityId?: string;
    entityType?: string;
    firstname?: string;
    lastname?: string;
    privacy?: boolean;
    role?: string;
    salutation?: string;
    schoolform?: NEWSLETTER_SCHOOLFORM;
};

const testData: Required<FormProps> = {
    entityId: "",
    entityType: "",
    role: "",
    schoolform: NEWSLETTER_SCHOOLFORM.sek,
    email: "max@mustermann.com",
    firstname: "Max",
    lastname: "Musterman",
    privacy: true,
    salutation: "Herr",
};

const defaultData: Required<FormProps> = {
    entityId: "",
    entityType: "",
    role: "",
    schoolform: NEWSLETTER_SCHOOLFORM.und,
    email: "",
    firstname: "",
    houseNumber: "5",
    lastname: "",
    privacy: false,
    salutation: "",
};

export const FormNewsletterModuleView = ({
    successHref,
    children,
    entityId,
    entityType,
    buttonLabel,
    roles,
    subjects,
}: PropsWithChildren<FormNewlsetterModuleProps>) => {
    const [error, setError] = useState<string | undefined>();

    const [form, setForm] = useState<FormProps>({
        ...defaultData,
        ...(env.NEXT_PUBLIC_USE_TESTDATA ? testData : {}),
        entityId,
        entityType,
    });

    const { executeRecaptcha } = useGoogleReCaptcha();

    const [processing, setProcessing] = useState(false);

    const router = useRouter();

    const handleChange = (values: FormProps) => {
        setError(undefined);
        setForm((f) => ({
            ...f,
            ...values,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!processing) {
            setProcessing(true);

            try {
                if (executeRecaptcha) {
                    const recaptchaToken = await executeRecaptcha(entityId);

                    const response = await fetch("/api/submit", {
                        method: "POST",
                        body: JSON.stringify({ ...form, recaptchaToken }),
                    });

                    if (response.ok && successHref) {
                        router.push(successHref);
                    } else {
                        throw new Error("Das schlug leider fehl.");
                    }
                } else {
                    throw new Error("Sendung aktuell nicht möglich");
                }
            } catch (error) {
                setError(toErrorString(error));
                setProcessing(false);
            }
        }
    };

    if (successHref) {
        return (
            <div className={twMerge("w-full grid")}>
                <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
                    <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                        <span
                            className={twMerge(
                                TYPO_STYLES.bodyExtraBold18(),
                                "col-span-6 text-brandDark",
                            )}
                        >
                            Kontaktdaten
                        </span>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3 sm:col-start-1 sm:col-end-3">
                            <label htmlFor="salutation" className="gap-2 flex flex-col">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Anrede
                                </span>
                                <select
                                    className={twMerge(INPUT_STYLES.dropdown())}
                                    onChange={(e) =>
                                        handleChange({ salutation: e.target.value || undefined })
                                    }
                                    defaultValue={form.salutation}
                                >
                                    <option value="">Auswählen</option>
                                    <option value="Herr">Herr</option>
                                    <option value="Frau">Frau</option>
                                    <option value="Divers">Divers</option>
                                </select>
                            </label>
                        </div>
                        <label className="flex flex-col gap-2 col-span-6 sm:col-start-1 col-end-4">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                Vorname
                            </span>
                            <input
                                type="text"
                                placeholder="Vorname"
                                className={INPUT_STYLES.input()}
                                defaultValue={form.firstname}
                                onChange={(e) =>
                                    handleChange({ firstname: e.target.value || undefined })
                                }
                                maxLength={getFormMaxlength("firstname")}
                                pattern=".*\S.*"
                                title="Bitte geben Sie einen gülten Wert ein."
                            />
                        </label>
                        <label className="flex flex-col gap-2 col-span-6 sm:col-span-3">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                Nachname
                            </span>
                            <input
                                type="text"
                                placeholder="Nachname"
                                className={INPUT_STYLES.input()}
                                defaultValue={form.lastname}
                                onChange={(e) =>
                                    handleChange({ lastname: e.target.value || undefined })
                                }
                                maxLength={getFormMaxlength("lastname")}
                                pattern=".*\S.*"
                                title="Bitte geben Sie einen gülten Wert ein."
                            />
                        </label>
                        <label className="grid col-span-6 gap-2">
                            <span
                                className={twMerge(TYPO_STYLES.bodyExtraBold18(), "text-brandDark")}
                            >
                                E-Mail
                            </span>
                            <input
                                required
                                type="email"
                                placeholder="E-Mail"
                                defaultValue={form.email}
                                className={INPUT_STYLES.input()}
                                onChange={(e) =>
                                    handleChange({ email: e.target.value || undefined })
                                }
                                maxLength={getFormMaxlength("email")}
                            />
                        </label>
                    </div>

                    <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                        <span
                            className={twMerge(
                                TYPO_STYLES.bodyExtraBold18(),
                                "col-span-6 text-brandDark",
                            )}
                        >
                            Mit den folgenden zusätzlichen Angaben, können wir Ihnen zielgerichtete
                            Angaben liefern
                        </span>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3">
                            <label htmlFor="roles" className="gap-2 flex flex-col">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Funktion
                                </span>
                                <select
                                    className={twMerge(INPUT_STYLES.dropdown())}
                                    onChange={(e) =>
                                        handleChange({ role: e.target.value || undefined })
                                    }
                                    defaultValue={form.role}
                                >
                                    <option value="">Auswählen</option>
                                    {Object.entries(roles).map(([key, value]) => (
                                        <option value={key} key={key}>
                                            {value}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3">
                            <label htmlFor="schoolform" className="flex flex-col gap-2">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Schulform
                                </span>
                                <select
                                    className={INPUT_STYLES.dropdown()}
                                    defaultValue={form.schoolform}
                                    onChange={(e) =>
                                        handleChange({
                                            schoolform: getEnumValue(
                                                e.target.value,
                                                NEWSLETTER_SCHOOLFORM,
                                                NEWSLETTER_SCHOOLFORM.und,
                                            ),
                                        })
                                    }
                                >
                                    <option value="">Auswählen</option>
                                    <option value="gs">Grundschule</option>
                                    <option value="sek">Sekundarschule</option>
                                </select>
                            </label>
                        </div>
                        {form.schoolform === NEWSLETTER_SCHOOLFORM.sek && (
                            <div className="gap-8 grid col-span-6">
                                {Object.entries(subjects).map(([key, value]) => {
                                    return (
                                        <div key={key} className="grid gap-2">
                                            <span
                                                className={twMerge(
                                                    TYPO_STYLES.bodyExtraBold18(),
                                                    "text-brandDark",
                                                )}
                                            >
                                                {key}
                                            </span>
                                            <div className="checkbox-formular relative gap-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ">
                                                {Object.entries(value).map(
                                                    ([subjKey, subValue]) => {
                                                        return (
                                                            <div
                                                                className="px-4 py-3 bg-greyOpacity05 rounded-lg"
                                                                key={subjKey}
                                                            >
                                                                <label htmlFor={subjKey}>
                                                                    <input
                                                                        id={subjKey}
                                                                        type="checkbox"
                                                                        checked={!!form[subjKey]}
                                                                        onChange={(e) =>
                                                                            handleChange({
                                                                                [subjKey]:
                                                                                    e.target
                                                                                        .checked,
                                                                            })
                                                                        }
                                                                    />
                                                                    {subValue}
                                                                    <span></span>
                                                                </label>
                                                            </div>
                                                        );
                                                    },
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {children && (
                        <div className="checkbox-formular relative">
                            <label className="no-marker">
                                <input
                                    defaultChecked={form.privacy}
                                    required
                                    type="checkbox"
                                    onChange={(e) => handleChange({ privacy: e.target.checked })}
                                />
                                {children}
                                <span></span>
                            </label>
                        </div>
                    )}
                    <div className={twMerge(WRAPPER_STYLES.HCenterRight(), "gap-6")}>
                        {error && (
                            <p className={twMerge(TYPO_STYLES.bodyNormal18(), "text-redError")}>
                                {error}
                            </p>
                        )}
                        <input
                            type="submit"
                            className={twMerge(BUTTON_STYLES.primary(), "cursor-pointer")}
                            value={processing ? "...wird gesendet" : buttonLabel}
                        />
                    </div>
                </form>
            </div>
        );
    } else {
        return <ErrorView>Success-Page is not provided.</ErrorView>;
    }
};
